/**
 * @format
 * @license SPDX-License-Identifier: Apache-2.0
 */

import React, {
	useState,
	useRef,
	useCallback,
	useEffect,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BlurText from "./BlurText";
import DotGrid from "../customCopmonent/DotGrid";
import ImageTrail from "../customCopmonent/ImageTrail";
import LightRays from "../customCopmonent/LightRays";
import WordListSwap from "../customCopmonent/WordListSwap";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";
import { useFitToWidth } from "@/hooks/useFitToWidth";

const HERO_VIDEO =
	"https://res.cloudinary.com/dcacqhspd/video/upload/v1779374923/gvixdyw4jrwqg5uwl00d.mp4";

/**
 * Phone-sized rendition of the hero film. The master asset is ~50 MB, which is
 * far more than a ~350px-wide frame can use; Cloudinary's `q_auto,w_720`
 * transform serves the same footage at ~7.6 MB. Paired with `preload="none"`
 * and a poster, a visitor who never taps play downloads only the poster.
 */
const HERO_VIDEO_MOBILE =
	"https://res.cloudinary.com/dcacqhspd/video/upload/q_auto,w_720,vc_auto/v1779374923/gvixdyw4jrwqg5uwl00d.mp4";

/** First frame of the film (~67 KB, format-negotiated). */
const HERO_POSTER =
	"https://res.cloudinary.com/dcacqhspd/video/upload/so_0,w_800,c_fill,q_auto,f_auto/v1779374923/gvixdyw4jrwqg5uwl00d.jpg";

/**
 * Tracks the >=1024px breakpoint. Initialised synchronously from `matchMedia`
 * so the correct hero renders on the very first paint — a `useEffect`-only
 * check would flash the mobile layout on desktop.
 */
function useIsDesktop() {
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window === "undefined" ?
			true
		:	window.matchMedia("(min-width: 1024px)").matches,
	);

	useEffect(() => {
		const mq = window.matchMedia("(min-width: 1024px)");
		const update = () => setIsDesktop(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);

	return isDesktop;
}

/**
 * Phone / tablet hero.
 *
 * The desktop composition below relies on a full-bleed background video and an
 * absolutely-positioned play control, neither of which survives a narrow
 * portrait viewport: the video was cropped to a sliver (or letterboxed into a
 * floating band), the control landed on top of the subtitle, and centring a
 * short block inside `min-h-screen` left large dead zones above and below.
 *
 * This layout instead reads top-to-bottom — eyebrow, headline, promise, action,
 * media — and gives the video its own 16:9 frame so the whole picture is
 * visible by construction rather than by cropping rules.
 */
function MobileHero() {
	const { t, i18n } = useTranslation();
	const shouldReduceMotion = useReducedMotion();
	const videoRef = useRef<HTMLVideoElement | null>(null);
	// Starts paused on purpose: nothing beyond the poster is fetched until the
	// visitor asks for the film.
	const [isPlaying, setIsPlaying] = useState(false);

	const rotatingWords = t("hero.rotatingWords", {
		returnObjects: true,
	}) as string[];
	const titleLine1 = t("hero.titleLine1");

	// One scale for the whole headline, small enough that the static line and
	// every rotating word each stay on a single line.
	const headlineRef = useRef<HTMLDivElement | null>(null);
	const headlineSize = useFitToWidth(headlineRef, [
		titleLine1,
		...rotatingWords,
	]);

	const togglePlayback = () => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused) {
			void video.play();
			setIsPlaying(true);
		} else {
			video.pause();
			setIsPlaying(false);
		}
	};

	// Height is content-driven. A `min-h-svh` shell centred a ~650px block in a
	// ~880px viewport, which is where the large empty bands above and below the
	// hero came from.
	return (
		<section className="relative isolate flex flex-col overflow-hidden bg-[#3b0092] font-sans selection:bg-white selection:text-black">
			{/* Ambient background — same texture as the desktop hero. */}
			<div className="absolute inset-0">
				<DotGrid
					dotSize={3}
					gap={14}
					baseColor="#2c016c"
					activeColor="#f09a38"
					proximity={90}
					shockRadius={160}
					shockStrength={3}
					resistance={750}
					returnDuration={1.5}
				/>
			</div>
			<div className="absolute inset-0 opacity-70">
				<LightRays
					raysOrigin="top-center"
					raysColor="#ffffff"
					raysSpeed={1}
					lightSpread={0.6}
					rayLength={2.4}
					followMouse={false}
					mouseInfluence={0}
					noiseAmount={0}
					distortion={0}
					pulsating={false}
					fadeDistance={1}
					saturation={1}
				/>
			</div>
			{/* Vignette keeps the copy legible over the brightest part of the rays. */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_75%_at_50%_0%,rgba(59,0,146,0)_35%,rgba(17,0,44,0.72)_100%)]"
			/>

			<div className="relative z-10 flex flex-col gap-4 px-5 pb-10 pt-24 text-white sm:gap-6 sm:px-8 sm:pb-14 sm:pt-28">
				{/* Eyebrow */}
				<motion.span
					initial={{ opacity: 0, y: shouldReduceMotion ? 0 : -10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
					className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-md">
					<span className="relative flex h-1.5 w-1.5">
						<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#f9a93a] opacity-70" />
						<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#f9a93a]" />
					</span>
					<span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
						{t("hero.eyebrow")}
					</span>
				</motion.span>

				{/* Headline — exactly two lines: the static half of the sentence, then
				    the rotating half beneath it. The two share one type scale so they
				    read as a single sentence, and `useFitToWidth` shrinks that scale
				    to whatever the longest of them needs, so neither ever wraps. The
				    static line used to break with "for" stranded on a line of its own,
				    and the rotating line needed a two-line height reservation because
				    the longer words wrapped and the section jolted on every rotation.
				    Both problems are the same problem, and sizing to fit is the fix. */}
				<div
					ref={headlineRef}
					className="flex flex-col text-[2rem] leading-[1.08] sm:text-5xl">
					<div style={headlineSize ? { fontSize: headlineSize } : undefined}>
						<BlurText
							key={`m-hero-title-${i18n.language}`}
							text={titleLine1}
							delay={90}
							animateBy="words"
							direction="top"
							nowrap
							className="justify-start font-display font-semibold leading-[1.08] tracking-[-0.03em]"
						/>
						<WordListSwap
							key={`m-hero-words-${i18n.language}`}
							texts={rotatingWords}
							nowrap
							mainClassName="justify-start font-display font-semibold leading-[1.08] tracking-[-0.03em] text-[#f9a93a]"
							staggerFrom="last"
							initial={{ y: "100%" }}
							animate={{ y: 0 }}
							exit={{ y: "-120%" }}
							staggerDuration={0.025}
							splitLevelClassName="overflow-hidden pb-1"
							transition={{ type: "spring", damping: 30, stiffness: 400 }}
							rotationInterval={2400}
						/>
					</div>
				</div>

				{/* Accent rule */}
				<div
					aria-hidden
					className="h-px w-16 bg-linear-to-r from-[#f9a93a] to-transparent"
				/>

				<motion.p
					initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.25 }}
					className="max-w-[38ch] text-[15px] leading-relaxed text-white/75">
					{t("hero.subtitle")}
				</motion.p>

				<motion.div
					initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.35 }}>
					<Link
						to="/contact-us"
						className="touch-target inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#2c016c] shadow-[0_12px_30px_-10px_rgba(0,0,0,0.6)] transition-transform duration-200 active:scale-[0.98] sm:w-auto">
						{t("cta.button")}
						<ArrowRight className="h-4 w-4" />
					</Link>
				</motion.div>

				{/* Video in a 16:9 frame — the complete picture, no cropping and no
				    letterboxed band floating in the middle of the screen. */}
				<motion.div
					initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.45 }}
					className="relative mt-1 w-full overflow-hidden rounded-2xl border border-white/15 bg-black/40 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.85)]">
					<button
						type="button"
						onClick={togglePlayback}
						aria-label={isPlaying ? t("hero.pauseVideo") : t("hero.playVideo")}
						className="group relative block aspect-video w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f9a93a]">
						<video
							ref={videoRef}
							src={HERO_VIDEO_MOBILE}
							poster={HERO_POSTER}
							loop
							playsInline
							preload="none"
							aria-hidden="true"
							className="h-full w-full object-cover"
						/>
						<span
							aria-hidden
							className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/55 via-black/10 to-transparent"
						/>

						{/* Paused: a large, obvious invitation over the poster.
						    Playing: it shrinks to an unobtrusive corner chip. */}
						{isPlaying ?
							<span
								aria-hidden
								className="absolute bottom-3 right-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-md transition-colors duration-200 group-active:bg-black/65">
								<Pause className="h-4 w-4" />
							</span>
						:	<span
								aria-hidden
								className="absolute inset-0 flex items-center justify-center">
								<span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur-md transition-transform duration-200 group-active:scale-95">
									{!shouldReduceMotion && (
										<span className="absolute inset-0 animate-ping rounded-full bg-white/20" />
									)}
									<Play className="relative ml-1 h-6 w-6" />
								</span>
							</span>
						}
					</button>
				</motion.div>
			</div>
		</section>
	);
}

/**
 * Desktop hero — type only.
 *
 * The film used to live here as a full-bleed background behind the headline,
 * toggled by a floating magnetic play button. Both are gone: the video now owns
 * the dedicated section directly below (`DesktopVideoSection`), where it can
 * be watched full screen instead of being read through a 65% black scrim.
 * Everything else about this composition is unchanged.
 */
function DesktopHero() {
	const { t, i18n } = useTranslation();

	// Rotating headline words come from the active locale (array of strings).
	const rotatingWords = t("hero.rotatingWords", {
		returnObjects: true,
	}) as string[];

	const boxRef = useRef<HTMLDivElement | null>(null);
	useGsapScrollParallax(boxRef);

	return (
		<div
			ref={boxRef}
			className="relative min-h-screen overflow-hidden font-sans selection:bg-white selection:text-black bg-[#3b0092]">
			{/* Ambient background */}
			<div className="absolute inset-0">
				<DotGrid
					dotSize={4}
					gap={15}
					baseColor="#2c016c"
					activeColor="#f09a38"
					proximity={100}
					shockRadius={200}
					shockStrength={3}
					resistance={750}
					returnDuration={1.5}
				/>
			</div>
			<div className="absolute inset-0">
				<LightRays
					raysOrigin="top-center"
					raysColor="#ffffff"
					raysSpeed={1}
					lightSpread={0.5}
					rayLength={3}
					followMouse={true}
					mouseInfluence={0.1}
					noiseAmount={0}
					distortion={0}
					className="custom-rays"
					pulsating={false}
					fadeDistance={1}
					saturation={1}
				/>
			</div>
			{/* Content */}
			<div className="relative z-10 text-white">
				<main className="px-4 sm:px-6 lg:px-8 xl:px-12 overflow-x-clip">
					<div className="mx-auto flex min-h-screen max-w-7xl items-center justify-center py-24 sm:py-28 lg:py-32">
						<motion.div
							initial={{ opacity: 0, y: 40, scale: 0.96 }}
							animate={{ opacity: 1, y: 0, scale: 1 }}
							transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
							className="relative z-20 flex w-full max-w-7xl flex-col items-center gap-8  lg:text-center sm:gap-10 lg:gap-16">
							{/* <motion.div
								initial={{ opacity: 0, y: -18 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.7, delay: 0.15 }}
								className={`rounded-full border px-4 py-2 text-xs font-medium uppercase tracking-[0.28em] sm:px-5 sm:py-2.5 sm:text-sm ${
									isVideoPlaying ?
										"border-white/20 bg-white/10 text-white/80 backdrop-blur-md"
									:	"border-slate-900/10 bg-white/80 text-slate-700 backdrop-blur-md"
								}`}>
								Engineered for mission-critical performance
							</motion.div> */}

							<div className="relative flex w-full flex-col  items-center gap-4 sm:gap-5">
								<BlurText
									key={`hero-title-${i18n.language}`}
									text={t("hero.titleLine1")}
									delay={140}
									animateBy="words"
									direction="top"
									className="justify-start  text-5xl font-semibold leading-[0.95] tracking-[-0.04em] sm:text-6xl md:text-7xl lg:text-8xl text-start md:text-center font-display"
								/>
								<div className="flex justify-start">
									<WordListSwap
										key={`hero-words-${i18n.language}`}
										texts={rotatingWords}
										mainClassName="justify-center text-center text-5xl font-semibold leading-[0.95] tracking-[-0.04em] sm:text-6xl md:text-7xl lg:text-8xl  font-display"
										staggerFrom={"last"}
										initial={{ y: "100%" }}
										animate={{ y: 0 }}
										exit={{ y: "-120%" }}
										staggerDuration={0.025}
										splitLevelClassName="overflow-hidden pb-0.5 sm:pb-1 md:pb-1"
										transition={{ type: "spring", damping: 30, stiffness: 400 }}
										rotationInterval={2000}
									/>
								</div>

							</div>

							<motion.div
								initial={{ opacity: 0, y: 24 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.85, delay: 0.35 }}
								className="max-w-4xl">
								<BlurText
									key={`hero-subtitle-${i18n.language}`}
									text={t("hero.subtitle")}
									delay={35}
									animateBy="words"
									direction="bottom"
									className="justify-center text-center text-base leading-relaxed sm:text-lg md:text-xl lg:text-2xl text-white/85"
								/>
							</motion.div>
						</motion.div>
					</div>
				</main>
			</div>
		</div>
	);
}

/** Prefixed spellings of the Fullscreen API, for older WebKit. */
type FullscreenFrame = HTMLDivElement & {
	webkitRequestFullscreen?: () => void;
};
type FullscreenVideo = HTMLVideoElement & {
	webkitEnterFullscreen?: () => void;
};
type FullscreenDocument = Document & {
	webkitFullscreenElement?: Element | null;
	webkitExitFullscreen?: () => void;
};

/**
 * Desktop film section, directly below the hero.
 *
 * Two states and nothing in between. At rest the film is a small rounded card
 * centred in a white band, at one fixed size for the whole of the section —
 * there is no scroll choreography here any more: no pinned runway, no card
 * that grows as the page moves. The only thing that changes it is the control
 * in its middle. Play takes the film full screen; pause brings the card back.
 *
 * "Full screen" is the real thing — `requestFullscreen()` on the frame —
 * rather than a `position: fixed` overlay, and that is not a matter of taste.
 * This app mounts GSAP ScrollSmoother (see providers/gsapProvider.jsx), which
 * moves the page by transforming `#smooth-content`; a transformed ancestor
 * becomes the containing block for every fixed descendant, so an `inset-0`
 * overlay declared in here would anchor itself to the moving content rather
 * than to the viewport and drift away as the page scrolled. A full-screen
 * element is promoted to the browser's top layer instead, which ignores
 * ancestor transforms, clipping and stacking entirely. It also keeps the very
 * same `<video>` element — so playback continues rather than restarting, as it
 * would if the node were re-parented into a portal — and brings Escape
 * handling with it for free.
 *
 * Sound needs no coaxing. Audible playback is gated on a user activation and a
 * click on the play control is one, so the film simply starts with sound; only
 * an explicit choice at the corner control overrides that, and it is
 * remembered for the next play.
 *
 * Nothing but the poster and the container metadata is fetched until the
 * visitor presses play, so a page view that never watches the film costs
 * ~67 KB rather than the ~50 MB master.
 */
function DesktopVideoSection() {
	const { t } = useTranslation();
	const shouldReduceMotion = useReducedMotion();
	const frameRef = useRef<HTMLDivElement | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	/**
	 * The visitor's own mute decision, or null if they have not made one. Kept
	 * in a ref so that starting the film reads the latest choice without the
	 * play handler having to be rebuilt whenever it changes.
	 */
	const userMutedRef = useRef<boolean | null>(null);
	/** True while this frame — not merely something on the page — is expanded. */
	const wasFullscreenRef = useRef(false);
	const [isMuted, setIsMuted] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);

	/**
	 * Full screen, by whichever name the browser knows it. The unprefixed API
	 * covers every desktop browser this layout is served to; `webkit` covers
	 * older Safari, and iPadOS — which lands here in landscape but only ever
	 * allows the video element itself to expand — falls back to
	 * `webkitEnterFullscreen`. If none of them exist the film simply plays in
	 * the card, which is a fair degradation rather than a broken control.
	 */
	const enterFullscreen = useCallback(() => {
		const frame = frameRef.current as FullscreenFrame | null;
		if (frame?.requestFullscreen) {
			void frame.requestFullscreen().catch(() => {});
			return;
		}
		if (frame?.webkitRequestFullscreen) {
			frame.webkitRequestFullscreen();
			return;
		}
		(videoRef.current as FullscreenVideo | null)?.webkitEnterFullscreen?.();
	}, []);

	// Both of these compare against the frame rather than just asking whether
	// anything is full screen, so this section never collapses — or pauses — on
	// account of some other element on the page entering or leaving.
	const exitFullscreen = useCallback(() => {
		const doc = document as FullscreenDocument;
		const frame = frameRef.current;
		if (doc.fullscreenElement === frame) {
			void doc.exitFullscreen().catch(() => {});
			return;
		}
		if (doc.webkitFullscreenElement === frame) doc.webkitExitFullscreen?.();
	}, []);

	/** Play and full screen are one action; so are pause and coming back. */
	const togglePlayback = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;

		if (!video.paused) {
			video.pause();
			exitFullscreen();
			return;
		}

		// This runs from a click, which is the user activation browsers require
		// before they will allow sound — so unlike the scroll-driven unmute this
		// section used to attempt, it is always permitted.
		const muted = userMutedRef.current ?? false;
		video.muted = muted;
		setIsMuted(muted);
		void video.play().catch(() => {});
		enterFullscreen();
	}, [enterFullscreen, exitFullscreen]);

	const toggleMuted = () => {
		const nextMuted = !isMuted;
		userMutedRef.current = nextMuted;
		setIsMuted(nextMuted);
		const video = videoRef.current;
		if (video) {
			video.muted = nextMuted;
			if (!nextMuted) void video.play().catch(() => {});
		}
	};

	// Leaving full screen by any route — Escape, the browser's own control, the
	// tab being switched away from — is the same intent as pausing, so the film
	// stops and the card returns to its resting size. Backgrounding the tab also
	// pauses outright, so a page nobody is looking at never decodes video.
	useEffect(() => {
		const doc = document as FullscreenDocument;

		const onFullscreenChange = () => {
			const frame = frameRef.current;
			if (
				doc.fullscreenElement === frame ||
				doc.webkitFullscreenElement === frame
			) {
				wasFullscreenRef.current = true;
				return;
			}
			if (!wasFullscreenRef.current) return;
			wasFullscreenRef.current = false;
			videoRef.current?.pause();
		};
		const onVisibilityChange = () => {
			if (!document.hidden) return;
			videoRef.current?.pause();
			exitFullscreen();
		};

		document.addEventListener("fullscreenchange", onFullscreenChange);
		document.addEventListener("webkitfullscreenchange", onFullscreenChange);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			document.removeEventListener("fullscreenchange", onFullscreenChange);
			document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [exitFullscreen]);

	return (
		<section
			aria-label={t("hero.videoSection", "Showreel")}
			className="relative flex min-h-screen w-full items-center justify-center bg-white px-6 py-24">
			<motion.div
				initial={shouldReduceMotion ? false : { opacity: 0, y: 28 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
				className="w-[clamp(600px,54vw,1200px)]">
				{/* The frame is the element that goes full screen, so everything that
				    has to be visible there lives inside it. The browser's own
				    full-screen rules take over its size and position; only the corner
				    radius and the video's fit are worth restating — a 16:9 film on a
				    16:10 screen should letterbox rather than lose its edges. */}
				<div
					ref={frameRef}
					className="group relative aspect-video w-full overflow-hidden rounded-3xl bg-black shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)] [&:fullscreen]:rounded-none [&:fullscreen_video]:object-contain">
					<video
						ref={videoRef}
						src={HERO_VIDEO}
						poster={HERO_POSTER}
						loop
						muted={isMuted}
						playsInline
						preload="metadata"
						onPlay={() => setIsPlaying(true)}
						onPause={() => setIsPlaying(false)}
						className="h-full w-full object-cover"
					/>

					{/* Keeps the controls legible over bright frames. */}
					<div
						aria-hidden
						className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/45 via-transparent to-transparent"
					/>

					{/* The whole frame is the play/pause target, so a click anywhere on
					    the film does what the centre button does. Once it is playing the
					    button fades out of the way and returns on hover or focus, rather
					    than sitting in the middle of a full-screen showreel. */}
					<button
						type="button"
						onClick={togglePlayback}
						aria-label={isPlaying ? t("hero.pauseVideo") : t("hero.playVideo")}
						className="group/control absolute inset-0 z-10 flex items-center justify-center focus-visible:outline-none">
						<span
							className={`relative inline-flex h-20 w-20 items-center justify-center rounded-full border border-white/30 bg-black/40 text-white backdrop-blur-md transition duration-300 group-hover/control:scale-105 group-hover/control:bg-black/60 group-focus-visible/control:ring-2 group-focus-visible/control:ring-[#f09a38] ${
								isPlaying ?
									"opacity-0 group-hover/control:opacity-100 group-focus-visible/control:opacity-100"
								:	"opacity-100"
							}`}>
							{!isPlaying && !shouldReduceMotion && (
								<span
									aria-hidden
									className="absolute inset-0 animate-ping rounded-full bg-white/20"
								/>
							)}
							{isPlaying ?
								<Pause className="relative h-7 w-7" />
							:	<Play className="relative ml-1 h-7 w-7" />}
						</span>
					</button>

					<button
						type="button"
						onClick={toggleMuted}
						aria-label={
							isMuted ?
								t("hero.unmuteVideo", "Unmute video")
							:	t("hero.muteVideo", "Mute video")
						}
						title={
							isMuted ?
								t("hero.unmuteVideo", "Unmute video")
							:	t("hero.muteVideo", "Mute video")
						}
						className="absolute bottom-6 right-6 z-20 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-black/40 text-white backdrop-blur-md transition-colors duration-200 hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38]">
						{isMuted ?
							<VolumeX className="h-5 w-5" />
						:	<Volume2 className="h-5 w-5" />}
					</button>

					{/* Status only — the film is driven by the control, not by this. */}
					<span
						aria-hidden
						className="pointer-events-none absolute bottom-6 left-6 z-20 inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80 backdrop-blur-md">
						<span className="h-1.5 w-1.5 rounded-full bg-[#f09a38]" />
						{isPlaying ?
							t("hero.nowPlaying", "Now playing")
						:	t("hero.showreel", "Showreel")}
					</span>
				</div>
			</motion.div>
		</section>
	);
}

export default function HeroSection() {
	// Two purpose-built compositions rather than one set of responsive
	// overrides. Desktop splits the hero into type and film: a text-only hero
	// followed by a film section the visitor plays full screen. On a phone that
	// pairing makes no sense — there is no full-screen API to lean on for an
	// element, and mobile data should not be spent on the full-size master — so
	// the phone layout keeps the film inline in its own 16:9 frame, tap-to-play,
	// exactly as before.
	const isDesktop = useIsDesktop();

	if (!isDesktop) return <MobileHero />;

	return (
		<>
			<DesktopHero />
			<DesktopVideoSection />
		</>
	);
}
